import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import MapaRuta from '../components/MapaRuta';
import { useFormatMoneda, useWhatsAppNumero } from '../utils/moneda';
import { mensajeRecordatorio } from '../utils/whatsapp';
import { useLicencia } from '../context/LicenciaContext';

const METODOS = ['efectivo', 'transferencia', 'nequi', 'daviplata', 'otro'];

export default function GuiaRuta() {
  const formatMoneda = useFormatMoneda();
  const numeroWhatsApp = useWhatsAppNumero();
  const { numeroEmpresa } = useLicencia();
  const { id } = useParams();
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [cobrosHoy, setCobrosHoy] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [cuotas, setCuotas] = useState([]);
  const [cobroForm, setCobroForm] = useState({ cuota_id: '', monto: '', metodo: 'efectivo', observaciones: '' });
  const [geoModal, setGeoModal] = useState(null);
  const [geoBuscar, setGeoBuscar] = useState('');
  const [geoResultados, setGeoResultados] = useState([]);
  const [vista, setVista] = useState('guia'); // 'guia' | 'mapa'
  const [loading, setLoading] = useState(false);
  const [recibo, setRecibo] = useState(null); // { cliente, monto, metodo, cuota, fecha }
  const [productosExpand, setProductosExpand] = useState(false);
  const [gestionModal, setGestionModal] = useState(null); // cliente
  const [gestionForm, setGestionForm] = useState({ motivo: 'cliente_ausente', fecha_promesa: '', observaciones: '' });
  const [gestionLoading, setGestionLoading] = useState(false);

  const cargar = useCallback(async () => {
    const [rutaRes, cobrosRes] = await Promise.all([
      api.get(`/rutas/${id}`),
      api.get('/cobros/mis-cobros', { params: { fecha: new Date().toISOString().split('T')[0] } }).catch(() => ({ data: [] }))
    ]);
    setRuta(rutaRes.data);
    setClientes(rutaRes.data.clientes || []);
    setCobrosHoy(cobrosRes.data);
  }, [id]);

  useEffect(() => { cargar(); }, [cargar]);

  async function abrirCobro(cliente) {
    setSeleccionado(cliente);
    setCobroForm({ cuota_id: '', monto: '', metodo: 'efectivo', observaciones: '' });
    setProductosExpand(false);
    if (cliente.credito_id) {
      try {
        const res = await api.get(`/creditos/${cliente.credito_id}/cuotas`);
        setCuotas(res.data.filter(c => c.estado !== 'pagada'));
      } catch {
        setCuotas([]);
        toast.error('Error cargando cuotas del crédito');
      }
    } else {
      setCuotas([]);
    }
  }

  async function registrarCobro(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const cuota = cuotas.find(c => c.id === parseInt(cobroForm.cuota_id));
      await api.post('/cobros', {
        cuota_id: parseInt(cobroForm.cuota_id),
        monto: parseFloat(cobroForm.monto),
        metodo: cobroForm.metodo,
        observaciones: cobroForm.observaciones
      });
      const clienteNombre = seleccionado.nombre;
      const clienteTel = seleccionado.telefono;
      setSeleccionado(null);
      setRecibo({
        cliente: clienteNombre,
        telefono: clienteTel,
        monto: parseFloat(cobroForm.monto),
        metodo: cobroForm.metodo,
        cuota: cuota ? `Cuota #${cuota.numero} (vence ${cuota.fecha_vence})` : '',
        fecha: new Date().toLocaleString('es-CO'),
        ruta: ruta?.nombre || ''
      });
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al registrar cobro');
    } finally {
      setLoading(false);
    }
  }

  async function registrarGestion(e) {
    e.preventDefault();
    setGestionLoading(true);
    try {
      await api.post('/gestiones', {
        cliente_id: gestionModal.id,
        motivo: gestionForm.motivo,
        fecha_promesa: gestionForm.motivo === 'promesa_de_pago' ? gestionForm.fecha_promesa : undefined,
        observaciones: gestionForm.observaciones || undefined,
      });
      toast.success('Gestión registrada');
      setGestionModal(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al registrar gestión');
    } finally {
      setGestionLoading(false);
    }
  }

  async function buscarGeo() {
    if (!geoBuscar) return;
    try {
      const res = await api.get(`/geocode?q=${encodeURIComponent(geoBuscar)}`);
      setGeoResultados(res.data);
    } catch {
      toast.error('Error buscando dirección');
    }
  }

  async function guardarCoordenadas(lat, lng) {
    await api.put(`/geocode/cliente/${geoModal.id}`, { lat, lng });
    toast.success('Coordenadas guardadas');
    setGeoModal(null);
    setGeoResultados([]);
    setGeoBuscar('');
    cargar();
  }

  const hoy = new Date().toISOString().split('T')[0];
  const clientesConCobro = new Set(
    cobrosHoy
      .filter(c => {
        const cl = clientes.find(cl => cl.credito_id === c.credito_id);
        return cl;
      })
      .map(c => c.credito_id)
  );

  const visitados = clientes.filter(c => clientesConCobro.has(c.credito_id)).length;
  const progreso = clientes.length > 0 ? Math.round((visitados / clientes.length) * 100) : 0;
  const sinCoordenadas = clientes.filter(c => !c.lat || !c.lng).length;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <button onClick={() => navigate('/rutas')} style={styles.backBtn}>← Rutas</button>
          <h2 style={styles.title}>{ruta?.nombre || 'Cargando...'}</h2>
          <span style={styles.sub}>{ruta?.cobrador_nombre || ''} · {clientes.length} clientes</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div style={styles.progressBox}>
            <span style={styles.progressLabel}>{visitados}/{clientes.length} cobrados</span>
            <div style={styles.progressTrack}>
              <div style={{ ...styles.progressFill, width: `${progreso}%` }} />
            </div>
            <span style={{ color: '#38bdf8', fontWeight: 700 }}>{progreso}%</span>
          </div>
          <div style={styles.tabs}>
            <button onClick={() => setVista('guia')} style={{ ...styles.tabBtn, ...(vista === 'guia' ? styles.tabActive : {}) }}>Guía</button>
            <button onClick={() => setVista('mapa')} style={{ ...styles.tabBtn, ...(vista === 'mapa' ? styles.tabActive : {}) }}>Mapa</button>
          </div>
        </div>
      </div>

      {sinCoordenadas > 0 && vista === 'mapa' && (
        <div style={styles.alertBanner}>
          {sinCoordenadas} cliente(s) sin coordenadas — Abre la guía y usa el botón "Ubicar" para asignar su posición en el mapa
        </div>
      )}

      {/* Vista Mapa */}
      {vista === 'mapa' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <MapaRuta clientes={clientes} cobrosHoy={cobrosHoy} />
        </div>
      )}

      {/* Vista Guía */}
      {vista === 'guia' && (
        <div style={styles.guiaWrap}>
          {clientes.length === 0 && (
            <p style={styles.empty}>Esta ruta no tiene clientes asignados</p>
          )}
          {clientes.map((cliente, i) => {
            const cobrado = clientesConCobro.has(cliente.credito_id);
            const vencido = (cliente.cuotas_vencidas || 0) > 0;
            return (
              <div key={cliente.id} style={{
                ...styles.clienteCard,
                borderLeft: `4px solid ${cobrado ? '#34d399' : vencido ? '#f87171' : '#334155'}`
              }}>
                <div style={styles.cardLeft}>
                  <div style={{ ...styles.numero, background: cobrado ? '#064e3b' : vencido ? '#7f1d1d' : '#1e293b', color: cobrado ? '#34d399' : vencido ? '#f87171' : '#94a3b8' }}>
                    {cobrado ? '✓' : i + 1}
                  </div>
                  <div>
                    <p style={styles.nombre}>{cliente.nombre}</p>
                    <p style={styles.doc}>{cliente.documento}</p>
                    {cliente.direccion && <p style={styles.dir}>{cliente.direccion}</p>}
                    {cliente.telefono && (
                      <a href={`https://wa.me/${numeroWhatsApp(cliente.telefono)}?text=${encodeURIComponent(mensajeRecordatorio(cliente, formatMoneda, numeroEmpresa))}`}
                        target="_blank" rel="noreferrer" style={styles.wapp}>
                        WhatsApp: {cliente.telefono}
                      </a>
                    )}
                    <div style={styles.badges}>
                      {cliente.saldo_pendiente > 0 && (
                        <span style={styles.badgeBlue}>Saldo: {formatMoneda(parseFloat(cliente.saldo_pendiente))}</span>
                      )}
                      {vencido && (
                        <span style={styles.badgeRed}>{cliente.cuotas_vencidas} vencida(s)</span>
                      )}
                      {cobrado && (
                        <span style={styles.badgeGreen}>Cobrado hoy</span>
                      )}
                      {!cliente.lat && (
                        <span style={styles.badgeYellow}>Sin ubicación</span>
                      )}
                      {!cobrado && cliente.proxima_cuota_fecha && (
                        <span style={cliente.proxima_cuota_dias < 0 ? styles.badgeRed : cliente.proxima_cuota_dias <= 3 ? styles.badgeYellow : styles.badgeBlue}>
                          Próx. cuota #{cliente.proxima_cuota_numero}: {formatMoneda(parseFloat(cliente.proxima_cuota_monto || 0))}
                          {cliente.proxima_cuota_dias < 0 ? ` (${Math.abs(cliente.proxima_cuota_dias)}d vencida)` : cliente.proxima_cuota_dias === 0 ? ' (hoy)' : ` (en ${cliente.proxima_cuota_dias}d)`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div style={styles.cardActions}>
                  <button style={styles.btnUbicar} onClick={() => { setGeoModal(cliente); setGeoBuscar(cliente.direccion || ''); }}>
                    {cliente.lat ? 'Re-ubicar' : 'Ubicar'}
                  </button>
                  {cliente.saldo_pendiente > 0 && (
                    <button style={styles.btnCobrar} onClick={() => abrirCobro(cliente)}>Cobrar</button>
                  )}
                  {!cobrado && (
                    <button style={styles.btnGestion} onClick={() => { setGestionModal(cliente); setGestionForm({ motivo: 'cliente_ausente', fecha_promesa: '', observaciones: '' }); }}>
                      Gestión
                    </button>
                  )}
                  {cliente.telefono && (
                    <a href={`tel:${cliente.telefono}`} style={styles.btnLlamar}>Llamar</a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal cobro */}
      {seleccionado && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Cobrar — {seleccionado.nombre}</h3>

            {/* Productos a crédito */}
            {seleccionado.productos_venta?.length > 0 && (
              <div style={{ marginBottom: '1.25rem', border: '1px solid #a78bfa44', borderRadius: 10, overflow: 'hidden' }}>
                <button
                  type="button"
                  onClick={() => setProductosExpand(v => !v)}
                  style={{ width: '100%', background: '#1a1040', border: 'none', padding: '0.65rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                >
                  <span style={{ color: '#a78bfa', fontWeight: 600, fontSize: '0.88rem' }}>
                    🛒 Productos a crédito ({seleccionado.productos_venta.length})
                  </span>
                  <span style={{ color: '#a78bfa', fontSize: '0.8rem' }}>{productosExpand ? '▲' : '▼'}</span>
                </button>
                {productosExpand && (
                  <div style={{ background: 'var(--bg-app)', padding: '0.75rem 1rem' }}>
                    {seleccionado.productos_venta.map((p, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: i < seleccionado.productos_venta.length - 1 ? '1px solid #1e293b' : 'none' }}>
                        <span style={{ color: 'var(--text-1s)', fontSize: '0.85rem' }}>• {p.nombre}</span>
                        <span style={{ color: 'var(--text-2)', fontSize: '0.82rem' }}>x{p.cantidad} · {formatMoneda(parseFloat(p.precio_unitario))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={registrarCobro}>
              <div style={styles.field}>
                <label style={styles.label}>Cuota</label>
                <select value={cobroForm.cuota_id} onChange={e => {
                  const cuota = cuotas.find(c => c.id === parseInt(e.target.value));
                  setCobroForm({ ...cobroForm, cuota_id: e.target.value, monto: cuota ? (cuota.monto - cuota.monto_pagado).toFixed(0) : '' });
                }} required style={styles.input}>
                  <option value="">Seleccionar cuota...</option>
                  {cuotas.map(cu => (
                    <option key={cu.id} value={cu.id}>
                      Cuota #{cu.numero} — Vence: {cu.fecha_vence} — Saldo: {formatMoneda(cu.monto - cu.monto_pagado)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Monto</label>
                <input type="number" step="0.01" value={cobroForm.monto} onChange={e => setCobroForm({ ...cobroForm, monto: e.target.value })} required style={styles.input} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Método</label>
                <select value={cobroForm.metodo} onChange={e => setCobroForm({ ...cobroForm, metodo: e.target.value })} style={styles.input}>
                  {METODOS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Observaciones</label>
                <input type="text" value={cobroForm.observaciones} onChange={e => setCobroForm({ ...cobroForm, observaciones: e.target.value })} style={styles.input} />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnCancel} onClick={() => setSeleccionado(null)}>Cancelar</button>
                <button type="submit" style={styles.btnPrimary} disabled={loading}>{loading ? 'Registrando...' : 'Registrar cobro'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal recibo de pago */}
      {recibo && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, textAlign: 'center', maxWidth: 400 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
            <h3 style={{ margin: '0 0 4px', color: '#4ade80', fontSize: '1.2rem' }}>Cobro registrado</h3>
            <p style={{ color: 'var(--text-3)', fontSize: '0.8rem', margin: '0 0 1.5rem' }}>{recibo.fecha}</p>

            <div style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '1rem', marginBottom: '1rem', textAlign: 'left' }}>
              {[
                ['Cliente', recibo.cliente],
                ['Monto', formatMoneda(recibo.monto)],
                ['Método', recibo.metodo],
                ['Cuota', recibo.cuota],
                ['Ruta', recibo.ruta],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.35rem 0', borderBottom: '1px solid #1e293b' }}>
                  <span style={{ color: 'var(--text-3)', fontSize: '0.82rem' }}>{k}</span>
                  <span style={{ color: 'var(--text-1)', fontSize: '0.85rem', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {recibo.telefono && (
                <a
                  href={`https://wa.me/${numeroWhatsApp(recibo.telefono)}?text=${encodeURIComponent(
                    `✅ *Recibo de pago - Prestam.io*\n\nCliente: ${recibo.cliente}\nMonto: ${formatMoneda(recibo.monto)}\nMétodo: ${recibo.metodo}\n${recibo.cuota}\nFecha: ${recibo.fecha}\n\n_Gracias por su pago._`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', padding: '0.65rem', borderRadius: 8, background: '#15803d', color: '#fff', fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  Enviar recibo por WhatsApp
                </a>
              )}
              <button
                onClick={() => setRecibo(null)}
                style={{ padding: '0.65rem', borderRadius: 8, background: 'var(--bg-hover)', color: 'var(--text-1)', border: 'none', cursor: 'pointer', fontWeight: 600 }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal gestión / no pago */}
      {gestionModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Registrar gestión — {gestionModal.nombre}</h3>
            <form onSubmit={registrarGestion}>
              <div style={styles.field}>
                <label style={styles.label}>Motivo de no pago</label>
                <select
                  value={gestionForm.motivo}
                  onChange={e => setGestionForm(f => ({ ...f, motivo: e.target.value, fecha_promesa: '' }))}
                  style={styles.input}
                >
                  <option value="cliente_ausente">Cliente ausente</option>
                  <option value="sin_dinero">Sin dinero</option>
                  <option value="en_disputa">En disputa</option>
                  <option value="promesa_de_pago">Promesa de pago</option>
                  <option value="direccion_incorrecta">Dirección incorrecta</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              {gestionForm.motivo === 'promesa_de_pago' && (
                <div style={styles.field}>
                  <label style={styles.label}>Fecha de promesa *</label>
                  <input
                    type="date"
                    required
                    min={new Date().toISOString().split('T')[0]}
                    value={gestionForm.fecha_promesa}
                    onChange={e => setGestionForm(f => ({ ...f, fecha_promesa: e.target.value }))}
                    style={styles.input}
                  />
                </div>
              )}
              <div style={styles.field}>
                <label style={styles.label}>Observaciones</label>
                <input
                  type="text"
                  placeholder="Detalles adicionales..."
                  value={gestionForm.observaciones}
                  onChange={e => setGestionForm(f => ({ ...f, observaciones: e.target.value }))}
                  style={styles.input}
                />
              </div>
              <div style={styles.modalActions}>
                <button type="button" style={styles.btnCancel} onClick={() => setGestionModal(null)}>Cancelar</button>
                <button type="submit" style={{ ...styles.btnPrimary, background: '#7c3aed' }} disabled={gestionLoading}>
                  {gestionLoading ? 'Guardando...' : 'Guardar gestión'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal ubicación */}
      {geoModal && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, maxWidth: '520px' }}>
            <h3 style={styles.modalTitle}>Ubicar: {geoModal.nombre}</h3>
            <p style={{ color: 'var(--text-2)', margin: '0 0 1rem', fontSize: '0.85rem' }}>
              Busca la dirección del cliente para guardar su posición en el mapa
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input
                type="text"
                value={geoBuscar}
                onChange={e => setGeoBuscar(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && buscarGeo()}
                placeholder="Ej: Calle 45 #10-20, Bogotá"
                style={{ ...styles.input, flex: 1 }}
              />
              <button style={styles.btnPrimary} onClick={buscarGeo}>Buscar</button>
            </div>
            {geoResultados.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {geoResultados.map((r, i) => (
                  <button key={i} style={styles.geoItem} onClick={() => guardarCoordenadas(r.lat, r.lng)}>
                    <span style={{ color: '#38bdf8', marginRight: '0.5rem' }}>📍</span>
                    {r.display}
                  </button>
                ))}
              </div>
            )}
            {geoModal.lat && (
              <p style={{ color: '#34d399', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                Coordenadas actuales: {geoModal.lat.toFixed(5)}, {geoModal.lng.toFixed(5)}
              </p>
            )}
            <div style={styles.modalActions}>
              <button style={styles.btnCancel} onClick={() => { setGeoModal(null); setGeoResultados([]); }}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '1rem' },
  backBtn: { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', padding: '0 0 0.25rem', fontSize: '0.85rem' },
  title: { margin: '0.25rem 0 0', color: 'var(--text-1)', fontSize: '1.4rem' },
  sub: { color: 'var(--text-2)', fontSize: '0.85rem' },
  progressBox: { display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-card)', borderRadius: '10px', padding: '0.6rem 1rem' },
  progressLabel: { color: 'var(--text-2)', fontSize: '0.85rem', whiteSpace: 'nowrap' },
  progressTrack: { width: '100px', height: '8px', background: 'var(--bg-hover)', borderRadius: '4px' },
  progressFill: { height: '8px', background: '#38bdf8', borderRadius: '4px', transition: 'width 0.5s' },
  tabs: { display: 'flex', background: 'var(--bg-card)', borderRadius: '8px', padding: '3px' },
  tabBtn: { background: 'none', border: 'none', color: 'var(--text-2)', padding: '0.4rem 1rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' },
  tabActive: { background: 'var(--bg-hover)', color: '#38bdf8', fontWeight: 700 },
  alertBanner: { background: '#92400e', color: '#fef3c7', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '1rem' },
  guiaWrap: { display: 'flex', flexDirection: 'column', gap: '0.75rem', overflowY: 'auto' },
  clienteCard: { background: 'var(--bg-card)', borderRadius: '10px', padding: '1rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' },
  cardLeft: { display: 'flex', gap: '1rem', flex: 1 },
  cardActions: { display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-end', flexShrink: 0 },
  numero: { width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 },
  nombre: { margin: 0, color: 'var(--text-1)', fontWeight: 600, fontSize: '0.95rem' },
  doc: { margin: '0.15rem 0 0', color: 'var(--text-2)', fontSize: '0.8rem' },
  dir: { margin: '0.15rem 0 0', color: 'var(--text-3)', fontSize: '0.8rem' },
  wapp: { color: '#25d366', fontSize: '0.8rem', textDecoration: 'none' },
  badges: { display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' },
  badgeBlue: { background: '#0c4a6e', color: '#38bdf8', borderRadius: '12px', padding: '0.15rem 0.6rem', fontSize: '0.75rem' },
  badgeRed: { background: '#7f1d1d', color: '#f87171', borderRadius: '12px', padding: '0.15rem 0.6rem', fontSize: '0.75rem' },
  badgeGreen: { background: '#064e3b', color: '#34d399', borderRadius: '12px', padding: '0.15rem 0.6rem', fontSize: '0.75rem' },
  badgeYellow: { background: '#78350f', color: '#fbbf24', borderRadius: '12px', padding: '0.15rem 0.6rem', fontSize: '0.75rem' },
  btnCobrar: { background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '8px', padding: '0.4rem 0.9rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', width: '100%' },
  btnUbicar: { background: 'var(--bg-hover)', color: 'var(--text-2)', border: 'none', borderRadius: '8px', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', width: '100%' },
  btnLlamar: { background: '#064e3b', color: '#34d399', border: 'none', borderRadius: '8px', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', textDecoration: 'none', textAlign: 'center', width: '100%', display: 'block' },
  btnGestion: { background: '#3b1f6e', color: '#a78bfa', border: 'none', borderRadius: '8px', padding: '0.35rem 0.7rem', cursor: 'pointer', fontSize: '0.8rem', width: '100%' },
  empty: { textAlign: 'center', color: 'var(--text-3)', padding: '3rem' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-card)', borderRadius: '12px', padding: '2rem', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle: { margin: '0 0 1.5rem', color: 'var(--text-1)' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' },
  field: { marginBottom: '1rem' },
  label: { display: 'block', color: 'var(--text-2)', fontSize: '0.85rem', marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.65rem', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-app)', color: 'var(--text-1)', fontSize: '0.95rem', boxSizing: 'border-box' },
  btnPrimary: { background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: 700, cursor: 'pointer' },
  btnCancel: { background: 'var(--bg-hover)', color: 'var(--text-1)', border: 'none', borderRadius: '8px', padding: '0.6rem 1.2rem', cursor: 'pointer' },
  geoItem: { display: 'block', width: '100%', background: 'var(--bg-app)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0.65rem', color: 'var(--text-1s)', cursor: 'pointer', textAlign: 'left', marginBottom: '0.5rem', fontSize: '0.85rem', lineHeight: '1.4' },
};
